import oneSignalService from './oneSignalService';
import { apiService } from './apiService';
import { env } from '@/config/environment';

export interface SavedMeeting {
  id: string;
  user_id: string;
  meeting_id: string;
  created_at: string;
  updated_at: string;
}

interface ReminderResult {
  success: boolean;
  error?: string;
  oneSignalNotificationId?: string;
  scheduledFor?: string;
}

export class SavedMeetingsService {
  
  static async getSavedMeetings(_userId: string): Promise<string[]> {
    try {
      console.log('📅 SavedMeetingsService: Loading saved meetings via apiService...');
      const response = await apiService.get('/api/meetings/saved');

      if (!response.success) {
        console.error('Error fetching saved meetings from API:', response.error);
        return [];
      }

      console.log('📅 SavedMeetingsService: Loaded', response.data?.meetingIds?.length || 0, 'saved meetings');
      return response.data?.meetingIds || [];
    } catch (error) {
      console.error('Failed to fetch saved meetings:', error);
      return [];
    }
  }

  private static async scheduleReminderAPI(params: {
    userId: string;
    meetingId: string;
    meetingName: string;
    meetingTime: string;
    meetingWeekday?: string;
    reminderTime: string;
    timeZone: string;
  }): Promise<ReminderResult> {
    try {
      console.log('📅 Scheduling meeting reminder:', { meetingId: params.meetingId, timeZone: params.timeZone, userId: params.userId });
      
      const response = await fetch(`${env.apiUrl}/api/meetings/schedule-reminder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: params.userId,
          meetingId: params.meetingId,
          meetingName: params.meetingName,
          meetingTime: params.meetingTime,
          meetingWeekday: params.meetingWeekday,
          reminderTime: params.reminderTime,
          timeZone: params.timeZone
        })
      });

      const result = await response.json();
      
      if (!response.ok) {
        console.error('❌ Meeting reminder scheduling failed:', result);
        return { success: false, error: result.error || 'Failed to schedule reminder' };
      }

      console.log('✅ Meeting reminder scheduled successfully:', result);
      return { 
        success: true, 
        oneSignalNotificationId: result.oneSignalNotificationId,
        scheduledFor: result.scheduledFor
      };
    } catch (error: any) {
      console.error('❌ Error scheduling meeting reminder:', error);
      return { success: false, error: error.message || 'Network error' };
    }
  }

  private static async cancelReminderAPI(userId: string, meetingId: string): Promise<ReminderResult> {
    try {
      console.log('🗑️ Canceling meeting reminder:', { meetingId, userId });
      
      const response = await fetch(`${env.apiUrl}/api/meetings/cancel-reminder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, meetingId })
      });

      const result = await response.json();
      
      if (!response.ok) {
        console.error('❌ Meeting reminder cancellation failed:', result);
        return { success: false, error: result.error || 'Failed to cancel reminder' };
      }

      console.log('✅ Meeting reminder canceled successfully');
      return { success: true };
    } catch (error: any) {
      console.error('❌ Error canceling meeting reminder:', error);
      return { success: false, error: error.message || 'Network error' };
    }
  }

  private static getNextMeetingOccurrence(meetingTime: string, meetingWeekdays: string[], userTimeZone: string): { reminderTime: string; meetingDateTime: string } | null {
    const WEEKDAY_MAP: { [key: string]: number } = {
      'sunday': 0, 'domingo': 0,
      'monday': 1, 'segunda-feira': 1, 'segunda': 1,
      'tuesday': 2, 'terça-feira': 2, 'terca-feira': 2, 'terça': 2, 'terca': 2,
      'wednesday': 3, 'quarta-feira': 3, 'quarta': 3,
      'thursday': 4, 'quinta-feira': 4, 'quinta': 4,
      'friday': 5, 'sexta-feira': 5, 'sexta': 5,
      'saturday': 6, 'sábado': 6, 'sabado': 6
    };

    const validDayIndices = meetingWeekdays
      .map(day => {
        const normalized = day.toLowerCase().trim().replace('-feira', '').replace('á', 'a').replace('ã', 'a').replace('ç', 'c');
        return WEEKDAY_MAP[day.toLowerCase().trim()] ?? WEEKDAY_MAP[normalized] ?? -1;
      })
      .filter(idx => idx >= 0);

    if (validDayIndices.length === 0) {
      console.log('⚠️ No valid weekdays found');
      return null;
    }

    const now = new Date();
    const timeParts = meetingTime.split(':').map(Number);
    const hours = timeParts[0] ?? 0;
    const minutes = timeParts[1] ?? 0;

    for (let dayOffset = 0; dayOffset <= 7; dayOffset++) {
      const candidate = new Date(now);
      candidate.setDate(candidate.getDate() + dayOffset);
      
      if (validDayIndices.includes(candidate.getDay())) {
        candidate.setHours(hours, minutes, 0, 0);
        
        if (candidate > now) {
          const reminderDate = new Date(candidate);
          reminderDate.setMinutes(reminderDate.getMinutes() - 10);
          
          console.log('📅 Next meeting occurrence found:', {
            meetingTime: candidate.toISOString(),
            reminderTime: reminderDate.toISOString(),
            dayOfWeek: candidate.toLocaleDateString('en-US', { weekday: 'short' }),
            timeZone: userTimeZone
          });
          
          return {
            meetingDateTime: candidate.toISOString(),
            reminderTime: reminderDate.toISOString()
          };
        }
      }
    }

    console.log('⚠️ No upcoming meeting occurrence found');
    return null;
  }

  static async saveMeeting(userId: string, meetingId: string): Promise<boolean> {
    try {
      console.log('📅 SavedMeetingsService: Saving meeting via apiService...');
      const response = await apiService.post('/api/meetings/saved', { meetingId });

      if (!response.success) {
        console.error('Error saving meeting via API:', response.error);
        return false;
      }

      const hasPushPermission = await oneSignalService.ensurePushPermission(userId);
      
      if (hasPushPermission) {
        // Get meeting data from the saved response or fetch via API
        const meetingResponse = await apiService.get(`/api/meetings/saved/${meetingId}`);
        const meetingData = meetingResponse.data?.meeting;

        if (meetingData) {
          const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo';
          const delimiter = meetingData.meeting_weekday?.includes(';') ? ';' : ',';
          const weekdays = meetingData.meeting_weekday?.split(delimiter).map((d: string) => d.trim()) || [];
          
          const nextOccurrence = this.getNextMeetingOccurrence(
            meetingData.meeting_time,
            weekdays,
            userTimeZone
          );

          if (nextOccurrence) {
            const reminderResult = await this.scheduleReminderAPI({
              userId,
              meetingId,
              meetingName: meetingData.meeting_name,
              meetingTime: meetingData.meeting_time,
              meetingWeekday: meetingData.meeting_weekday,
              reminderTime: nextOccurrence.reminderTime,
              timeZone: userTimeZone
            });

            if (!reminderResult.success) {
              console.warn('⚠️ Meeting saved but reminder scheduling failed:', reminderResult.error);
            } else {
              console.log('✅ Meeting saved and reminder scheduled successfully');
            }
          }
        }
      } else {
        console.log('📱 Push notifications not enabled - meeting saved without reminder');
      }

      return true;
    } catch (error) {
      console.error('Failed to save meeting:', error);
      return false;
    }
  }

  static async removeSavedMeeting(userId: string, meetingId: string): Promise<boolean> {
    try {
      console.log('🗑️ Canceling reminder for removed meeting:', meetingId);
      const cancelResult = await this.cancelReminderAPI(userId, meetingId);

      if (!cancelResult.success) {
        console.warn('⚠️ Reminder cancellation failed (will still remove meeting):', cancelResult.error);
      } else {
        console.log('✅ Reminder canceled successfully');
      }

      console.log('📅 SavedMeetingsService: Removing meeting via apiService...');
      const response = await apiService.delete(`/api/meetings/saved/${meetingId}`);

      if (!response.success) {
        console.error('Error removing saved meeting via API:', response.error);
        return false;
      }

      console.log('✅ Meeting removed and reminder canceled successfully');
      return true;
    } catch (error) {
      console.error('Failed to remove saved meeting:', error);
      return false;
    }
  }

  static async isMeetingSaved(_userId: string, meetingId: string): Promise<boolean> {
    try {
      console.log('📅 SavedMeetingsService: Checking if meeting is saved via apiService...');
      const response = await apiService.get(`/api/meetings/saved/${meetingId}`);

      if (!response.success) {
        console.error('Error checking saved meeting via API:', response.error);
        return false;
      }

      return response.data?.isSaved || false;
    } catch (error) {
      console.error('Failed to check if meeting is saved:', error);
      return false;
    }
  }

  static async toggleSavedMeeting(userId: string, meetingId: string): Promise<{ success: boolean, isSaved: boolean }> {
    try {
      const isSaved = await this.isMeetingSaved(userId, meetingId);
      
      if (isSaved) {
        const success = await this.removeSavedMeeting(userId, meetingId);
        return { success, isSaved: false };
      } else {
        const success = await this.saveMeeting(userId, meetingId);
        return { success, isSaved: true };
      }
    } catch (error) {
      console.error('Failed to toggle saved meeting:', error);
      return { success: false, isSaved: false };
    }
  }
}
